import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { isGerman } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';
import type { Baby } from '@/types';
import {
  tipOfTheDayFor,
  localeTip,
} from '@/data/daily-tips';
import {
  recipeOfTheDayFor,
  localeRecipe,
  localeAllergenLabel,
} from '@/data/nutrition-recipes';

interface Props {
  baby: Baby;
}

// ────────────────────────────────────────────────────────────────────
// DailyBriefingCard — the "Heute" surface on /today.
//
// Shows two stacked sections:
//   1. Tip of the day / Wissenswertes — age-appropriate, rotates daily
//   2. Today's recipe — pulled from the nutrition library, rotates daily
//
// Both rotate deterministically by (baby_id, date). Same baby + same
// day = same content for everyone in the household. Next day rotates.
//
// Skips the recipe section gracefully if the baby is under 6 months
// (milk-only stage; no solids to recommend).
// ────────────────────────────────────────────────────────────────────

export function DailyBriefingCard({ baby }: Props) {
  const de = isGerman();

  const { ageWeeks, tip, recipe, dateLabel } = useMemo(() => {
    const dob = new Date(baby.date_of_birth + 'T00:00:00Z');
    const days = Math.max(0, Math.floor((Date.now() - dob.getTime()) / 86400000));
    const weeks = Math.floor(days / 7);

    const now = new Date();
    const t = tipOfTheDayFor(weeks, baby.id, now);
    const r = recipeOfTheDayFor(weeks, baby.id, now);

    const locale = de ? 'de-DE' : 'en-US';
    const label = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(now);

    return {
      ageWeeks: weeks,
      tip: t ? localeTip(t) : null,
      recipe: r ? localeRecipe(r) : null,
      dateLabel: label,
    };
  }, [baby.id, baby.date_of_birth, de]);

  // Nothing meaningful to show. Don't render an empty card.
  if (!tip && !recipe) return null;

  const heading = de ? 'Heute' : 'Today';

  // "More about this" CTA — sends the user into chat seeded with a
  // question framed around the tip title.
  const moreAboutTipUrl = tip
    ? `/chat?prompt=${encodeURIComponent(
        (de ? 'Erzähl mir mehr zu: ' : 'Tell me more about: ') + tip.title
      )}`
    : null;

  return (
    <section
      className="rounded-2xl bg-gradient-to-br from-primary/[0.06] via-cream to-accent/[0.08] border border-primary-100 p-5 space-y-4"
      aria-label={heading}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-xl font-semibold text-slate">{heading}</h2>
        <span className="text-xs text-slate-muted capitalize">{dateLabel}</span>
      </div>

      {tip && (
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-primary-700 uppercase tracking-wide mb-1.5">
            {tip.kind === 'tipp' ? <Icon.Lightbulb size={14} /> : <Icon.Sparkles size={14} />}
            <span>{tip.kindLabel}</span>
          </div>
          <h3 className="font-display font-semibold text-slate leading-snug">
            {tip.title}
          </h3>
          <p className="text-sm text-slate mt-1.5 leading-relaxed">{tip.body}</p>
          {moreAboutTipUrl && (
            <Link
              to={moreAboutTipUrl}
              className="inline-block text-xs text-primary-700 mt-2 hover:text-primary-800 hover:underline"
            >
              {de ? 'Alma fragen →' : 'Ask Alma →'}
            </Link>
          )}
        </div>
      )}

      {tip && recipe && <div className="border-t border-primary-50" />}

      {recipe && (
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-accent-600 uppercase tracking-wide mb-1.5">
            <Icon.Bowl size={14} />
            <span>{de ? 'Gericht des Tages' : "Today's recipe"}</span>
          </div>
          <h3 className="font-display font-semibold text-slate leading-snug">
            {recipe.title}
          </h3>
          <p className="text-sm text-slate mt-1.5 leading-relaxed">
            {recipe.whyForThisAge}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-muted">
            <span className="font-medium">
              {recipe.prepMinutes + recipe.cookMinutes} min
            </span>
            {recipe.allergens.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>
                  {(de ? 'Allergene: ' : 'Allergens: ') +
                    recipe.allergens.map(localeAllergenLabel).join(', ')}
                </span>
              </>
            )}
          </div>
          <Link
            to="/nutrition"
            className="inline-block text-xs text-primary-700 mt-2 hover:text-primary-800 hover:underline"
          >
            {de ? 'Zum Rezept →' : 'See full recipe →'}
          </Link>
        </div>
      )}

      {/* Under-6-month edge case: only a tip, no recipe. Add a soft hint
          that recipes will arrive once solids start. */}
      {tip && !recipe && ageWeeks < 26 && (
        <div className="flex items-start gap-1.5 text-xs text-slate-muted pt-1">
          <Icon.Spoon size={14} className="shrink-0 mt-0.5" />
          <span>
            {de
              ? 'Sobald die Beikost startet (etwa 6 Monate), kommt hier täglich ein Rezept dazu.'
              : 'Once solids start (around 6 months), a daily recipe will appear here too.'}
          </span>
        </div>
      )}
    </section>
  );
}
